import Hex from "crypto-js/enc-hex";
import SHA256 from "crypto-js/sha256";
import queryString from "query-string";

import type { ResponseFlightFares } from "@/libs/client/goApi/flight/flightServiceInterfaces/getFlightFaresInterface";
import type {
  RequestHotelAvailableInterface,
  ResponseHotelAvailablePresenter,
} from "@/libs/client/goApi/hotel/hotelServiceInterfaces/getHotelAvailableInterface";
import type { RequestFlightFaresInterface } from "@/types/product/flights/flightsType";

import { queryStringToObject } from "../routeHelper/routeUrlUtills";

export function checkExistValue<T>(value: T): boolean {
  if (value === null) return false;
  if (value === undefined) return false;
  if (value === "") return false;
  if (value === "null") return false;
  if (value === "undefined") return false;
  if (value === 0) return false;
  if (value === "0") return false;
  return true;
}

export function checkEmptyValue<T>(value: T): boolean {
  const validateCase = ["null", "undefined", "0", "false", "NaN", ""];
  if (!value) return false;
  if (typeof value === "string" && validateCase.includes(value.trim()))
    return false;
  return true;
}

export const checkExistObject = (
  data: Object,
  option?: { isProps?: boolean },
): boolean => {
  if (!data) {
    return false;
  }
  return Object.entries(data).some(([key, value]) => {
    if (typeof value === "object") {
      return checkExistObject(value, option);
    }
    if (typeof value === "function") {
      return false;
    }
    if (option?.isProps) {
      const ignorePropsKey = ["children", "className", "style", "classnames"];
      return !ignorePropsKey.includes(value);
    }

    return !!value;
  });
};

export function checkExistValueAll<T>(
  value: T | null | undefined,
): value is NonNullable<T> {
  if (value === null || value === undefined) return false;

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (typeof value === "object") {
    return checkExistObject(value);
  }

  if (value === "" || value === "null" || value === "undefined") return false;

  return true;
}

export function checkExistArray<T>(value: T): boolean {
  return (
    value !== null &&
    value !== undefined &&
    Array.isArray(value) &&
    value.length !== 0
  );
}
export function checkPhone(phone: string, prefix: string) {
  if (prefix === "66") {
    return phone.slice(3, 4) === "0" ? phone.replace("0", "") : phone;
  }
  return phone;
}

export function checkMaxLengthPhone(prefix: string) {
  if (prefix === "66") {
    return 13;
  }
  return 17;
}

export function validEmail(email: string) {
  const re = /\S+@\S+\.\S+/;
  return re.test(email);
}

export const validateInput = (
  type: "email" | "text" | "middleName" | "lastName",
  value: string,
): boolean => {
  const reEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  switch (type) {
    case "email":
      return reEmail.test(String(value).toLowerCase());
    case "text":
      return value.trim() !== "";
    case "middleName":
      return true;
    case "lastName":
      return value.trim() !== "";
    default:
      return false;
  }
};

export const validateFullName = (first: string, middle: string) => {
  if (!first.trim()) {
    return false;
  }
  if (middle.trim() && !/^[a-zA-Z]+$/.test(middle.trim())) {
    return false;
  }
  return true;
};

export function validPassword(password: string) {
  const re = /^([a-zA-Z0-9@$!%*?&%#._-]{8,})$/;
  return re.test(password);
}

export function isValidDate(dateObject: Date | undefined | null) {
  return !!dateObject && dateObject.toString() !== "Invalid Date";
}

// Function to remove the 'session' parameter from the query string
export const removeSessionParameter = (
  queryStringValue: string,
  valueToRemove?: string,
): string => {
  // Split the query string into individual parameters
  const params = queryStringValue.split("&");

  // Filter out the 'session' parameter, the 'destinationDisplay' parameter, and the valueToRemove if provided
  const filteredParams = params.filter((param) => {
    if (valueToRemove && param.startsWith(`${valueToRemove}=`)) {
      return false; // Exclude the parameter if it starts with the specified valueToRemove
    }

    return (
      !param.startsWith("session=") &&
      !param.startsWith("options=") &&
      !param.startsWith("utmid=") &&
      !param.startsWith("session_id=") &&
      !param.startsWith("destinationDisplay=") &&
      !param.startsWith("=0")
    );
  });

  // Join the remaining parameters back together
  return filteredParams.join("&");
};

// Function to create session from query parameters
export const createSession = (queryStringValue: string): string | undefined => {
  // Remove session parameter if it exists
  const queryStringWithoutSession = removeSessionParameter(queryStringValue);

  if (!queryStringWithoutSession) return undefined;
  const hash = SHA256(queryStringWithoutSession).toString(Hex);

  return hash;
};

// Function to check if session matches the query parameters
export const checkSession = (queryStringValue: string): boolean => {
  const queryParams = new URLSearchParams(queryStringValue);

  // Get the original session parameter from the query string
  const originSession = queryParams.get("session");

  // Remove the 'session' parameter from the query string before sessioning
  const queryStringWithoutSession = removeSessionParameter(queryStringValue);
  const sessionParams = createSession(queryStringWithoutSession);
  return sessionParams === originSession;
};

export const createQueryStringWithSessionDifferentQueryStringAndSession = (
  sessionEncode: object | string,
  paramsEncode: object | string,
): string => {
  const queryStringOriginal =
    typeof paramsEncode === "string"
      ? paramsEncode
      : queryString.stringify(paramsEncode);

  const session =
    typeof sessionEncode === "string"
      ? sessionEncode
      : queryString.stringify(sessionEncode);

  const searchParams = new URLSearchParams(queryStringOriginal);
  const sessionParams = new URLSearchParams(session);

  // Sort the key/value pairs
  searchParams.sort();
  sessionParams.sort();

  const sortedQueryString = searchParams.toString();
  const sessionString = sessionParams.toString();

  const sessionFromQueryString = createSession(sessionString);

  const queryStringWithSession = `${sortedQueryString}&session=${sessionFromQueryString}`;

  return queryStringWithSession;
};

export const createQueryStringWithSession = (
  params: object | string,
): string => {
  const queryStringOriginal =
    typeof params === "string" ? params : queryString.stringify(params);

  const searchParams = new URLSearchParams(queryStringOriginal);

  // Sort the key/value pairs
  searchParams.sort();

  const sortedQueryString = searchParams.toString();

  const sessionFromQueryString = createSession(sortedQueryString);

  const queryStringWithSession = `${sortedQueryString}&session=${sessionFromQueryString}`;

  return queryStringWithSession;
};

export const isRequestInterface = <T>(obj: any): obj is T => {
  return obj && typeof obj === "object";
};

export function isRequestFlightFaresInterface(
  obj: any,
): obj is RequestFlightFaresInterface {
  return obj && typeof obj === "object";
}

export function isRequestHotelAvailableInterface(
  obj: any,
): obj is RequestHotelAvailableInterface {
  return obj && typeof obj === "object";
}

export function isResponseHotelAvailablePresenter(
  obj: any,
): obj is ResponseHotelAvailablePresenter {
  return obj && typeof obj === "object";
}

export function isResponseFlightFares(obj: any): obj is ResponseFlightFares {
  return obj && typeof obj === "object";
}

function countSlashes(input: string, slashKey: string = "/"): number {
  return input.split(slashKey).length - 1;
}

export function removeFirstSegment(
  inputString: string,
  slashKey: string = "/",
): string {
  if (countSlashes(inputString) === 1) {
    return "";
  }

  const firstSlashIndex = inputString.indexOf(slashKey);

  if (firstSlashIndex !== -1) {
    const secondSlashIndex = inputString.indexOf(slashKey, firstSlashIndex + 1);
    if (secondSlashIndex !== -1) {
      return inputString.substring(secondSlashIndex + 1);
    }
    return inputString.substring(firstSlashIndex + 1); // Return the input string from the first '/' if the second '/' is not found
  }
  return inputString; // Return the input string as is if '/' is not found
}

export function removeTrailingSlash(
  inputString: string,
  slashKey: string = "/",
): string {
  // Check if the last character is a '/' and remove it
  if (inputString.endsWith(slashKey)) {
    return inputString.slice(0, -1);
  }

  return inputString;
}

export function checkQueryParams(
  queryParam: string | object,
  flied: string[],
): boolean {
  let paramObj = {};

  // check string or object
  if (typeof queryParam === "string") {
    paramObj = queryStringToObject(queryParam);
  } else {
    paramObj = queryParam; // obj
  }
  const isHashingCorrected = checkSession(queryString.stringify(paramObj));

  if (paramObj) {
    return (
      // eslint-disable-next-line no-prototype-builtins
      flied.every((key) => paramObj?.hasOwnProperty(key)) && isHashingCorrected
    );
  }

  return false;
}

export const isDateComplete = (val: string) => !val?.split("-")?.includes("");
